import { createContext, ReactNode, useState, useEffect } from "react";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { HealthTeam } from "@/@types/esus/HealthTeam";
import { getHealthTeams } from "@/services/esus/healthTeamService";
import { getProfessionals } from "@/services/esus/professionalService";
import { Professional } from "@/@types/esus/Professional";
import { HealthUnit } from "@/@types/esus/HealthUnit";
import { getHealthUnits } from "@/services/app/healthUnitService";

type EsusContextType = {
	healthTeams: HealthTeam[];
	professionals: Professional[];
	units: HealthUnit[];
};

export const EsusContext = createContext({} as EsusContextType);

type EsusContextProviderProps = {
	children: ReactNode;
};

export function EsusContextProvider({ children }: EsusContextProviderProps) {
	const { uf, ibgeCode, cnes, module } = useCurrentAccount();
	const [healthTeams, setHealthTeams] = useState<HealthTeam[]>([]);
	const [professionals, setProfessionals] = useState<Professional[]>([]);
	const [units, setUnits] = useState<HealthUnit[]>([]);

	async function handleGetHealthTeams(uf: string, ibgeCode: string, cnes?: string) {
		setHealthTeams(await getHealthTeams({ uf, ibgeCode, cnes }));
	}

	async function handleGetProfessionals(uf: string, ibgeCode: string, cnes?: string) {
		setProfessionals(await getProfessionals({ uf, ibgeCode, cnes }));
	}

	async function handleGetHealthUnits(uf: string, ibgeCode: string, cnes?: string) {
		setUnits(await getHealthUnits({ uf, ibgeCode, cnes, module }));
	}

	useEffect(() => {
		if (uf && ibgeCode) {
			handleGetHealthUnits(uf, ibgeCode, cnes);
		}
		return () => {
			setUnits([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ibgeCode, cnes]);

	useEffect(() => {
		if (uf && ibgeCode) {
			handleGetHealthTeams(uf, ibgeCode, cnes);
			handleGetProfessionals(uf, ibgeCode, cnes);
		}
		return () => {
			setHealthTeams([]);
			setProfessionals([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<EsusContext.Provider value={{ healthTeams, professionals, units }}>
			{children}
		</EsusContext.Provider>
	);
}
